import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ISeoResutlsPage,
  ISeoResultManufacturerEntity,
} from '~/data/types/seo/seoResutlsPages';
import { UsedCarType } from '~/data/types/usedCar/usedCarData';
import { CarCardType } from '~/data/types/car/cars';
import { useIsLeaseCar } from '~/hooks/useIsLeaseCar';
import { ROUTES } from '~/data/routes';
import { useIsUsedCar } from './useIsUsedCar';
import { useAppSelector } from '~/redux-toolkit/store/hooks';
import { RootState } from '~/redux-toolkit/store/store';

interface UseGetSeoContentProps<T extends keyof ISeoResultManufacturerEntity> {
  propertyKeys: T[];
  propertyValues: string[][];
  seoContent?: ISeoResutlsPage;
  cars?: (UsedCarType | CarCardType)[];
  type: string;
}

const useGetSeoContent = <T extends keyof ISeoResultManufacturerEntity>({
  propertyKeys = [],
  propertyValues,
  seoContent,
  cars,
  type,
}: UseGetSeoContentProps<T>) => {
  const [shouldDisplay, setShouldDisplay] = useState(true);
  const isLeaseCar = useIsLeaseCar();
  const isUsedCar = useIsUsedCar();

  const { seoPage } = useAppSelector((state: RootState) => state.usedCarSlice);

  // Check if there's a single brand filter
  const hasSingleBrandFilter = useMemo(
    () => propertyValues[0]?.length === 1,
    [propertyValues],
  );

  // Check if there's a single model filter
  const hasSingleModelFilter = useMemo(
    () => propertyValues[1]?.length === 1,
    [propertyValues],
  );

  // Get the property content based on property values and SEO content
  const getPropertyContent = useCallback(
    (propertyValues: string[][], seoContent?: ISeoResutlsPage) => {
      if (!seoContent || !propertyValues[0]?.length) return undefined;
      const selectedValue = propertyValues[0][0];

      return seoContent?.seoManufacturerContent?.find(
        (content) => content[propertyKeys[0]] === selectedValue,
      );
    },
    [propertyKeys],
  );

  // Get the model description.
  const getModelDescription = useCallback(
    () => () => {
      if (!cars?.length) return '';

      return isUsedCar
        ? seoPage?.seoDesc
        : decodeURIComponent(
            (cars[0] as CarCardType)?.carProgrammaticDescription || '',
          );
    },
    [isUsedCar, seoPage, cars],
  );

  // Get the model title.
  const getModelTitle = useCallback(() => {
    if (!cars?.length) return '';
    if (isUsedCar) return seoPage?.seoTitle;
    const makeName = cars[0].carZoneMakeName || cars[0].carZoneManufacturerName;
    const modelName = cars[0].carZoneModelName;

    const propertyContent = seoContent?.seoManufacturerContent?.find(
      (content) => content[propertyKeys[0]] === makeName,
    );

    if (propertyContent?.title) {
      const words = propertyContent.title.split(' ');
      let insertionIndex;

      if (isLeaseCar) {
        insertionIndex = words.length - 2;
      } else if (type === ROUTES.USED_CAR) {
        insertionIndex = words.length - 3;
      } else {
        insertionIndex = words.length - 1;
      }

      return [
        ...words.slice(0, insertionIndex),
        modelName,
        ...words.slice(insertionIndex),
      ].join(' ');
    }
    return `${makeName} ${modelName}`;
  }, [
    cars,
    seoContent?.seoManufacturerContent,
    propertyKeys,
    isLeaseCar,
    type,
    isUsedCar,
    seoPage?.seoTitle,
  ]);

  useEffect(() => {
    const updateShouldDisplay = () => {
      if (hasSingleModelFilter && cars && cars?.length) {
        const modelDescription = getModelDescription();
        hasSingleBrandFilter
          ? setShouldDisplay(!!modelDescription)
          : setShouldDisplay(false);
      } else if (hasSingleBrandFilter && !hasSingleModelFilter) {
        const propertyContent = getPropertyContent(propertyValues, seoContent);
        setShouldDisplay(
          !!propertyContent?.title && !!propertyContent?.paragraph,
        );
      } else {
        setShouldDisplay(true);
      }
    };

    updateShouldDisplay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasSingleModelFilter,
    cars,
    hasSingleBrandFilter,
    propertyValues,
    seoContent,
    type,
    getPropertyContent,
    getModelDescription,
  ]);

  const seoBannerDescription = useMemo(() => {
    if (hasSingleModelFilter && cars?.length) {
      return getModelDescription();
    }
    if (hasSingleBrandFilter) {
      const propertyContent = getPropertyContent(propertyValues, seoContent);
      return propertyContent?.paragraph || '';
    }
    return seoContent?.paragraphRowCard || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasSingleModelFilter,
    getModelDescription,
    cars,
    hasSingleBrandFilter,
    propertyValues,
    getPropertyContent,
    seoContent,
  ]);

  const seoBannerTitle = useMemo(() => {
    if (hasSingleModelFilter && cars?.length) {
      return getModelTitle();
    }
    if (hasSingleBrandFilter) {
      const propertyContent = getPropertyContent(propertyValues, seoContent);
      return propertyContent?.title || '';
    }
    return seoContent?.titleRowCard || '';
  }, [
    hasSingleModelFilter,
    cars,
    hasSingleBrandFilter,
    seoContent,
    getModelTitle,
    getPropertyContent,
    propertyValues,
  ]);

  return {
    shouldDisplay,
    seoBannerTitle,
    seoBannerDescription,
  };
};

export default useGetSeoContent;
